/* eslint-disable prettier/prettier */
import { BaseView, ViewerType } from "./base_viewer.js";
import { TW_DATA_TAG, tw_disableBtn } from "./util.js";
import { CSS_UNITS } from "./ui_utils.js";
import { PDFHistory } from "./pdf_history.js";
import { RenderingCancelledException } from "./pdfjs-lib.js";
import { TextLayerBuilder } from "./text_layer_builder.js";

// eslint-disable-next-line sort-imports
import { AnnotationLayerBuilder } from "./annotation_layer_builder.js";

/**
 * SingleViewer displays a single page of a PDF document.
 * @extends BaseView
 * @ignore
 */
class SingleViewer extends BaseView {
  constructor(app) {
    super(app);
    this.type = ViewerType.SINGLE_VIEW;
    app.elements.tw2018elem_multiViewer.innerHTML = "";
    this._singleViewPageIsRendering = false;
    this._singleViewPendingPageNum = null;

    this.history = new PDFHistory({
      previousVisitedBtn: app.elements.tw2018elem_previousvisitedBtn,
      nextVisitedBtn: app.elements.tw2018elem_nextvisitedBtn,
    });
    this.cleanPage();
  }

  dispose() {
    this.cleanPage();
    this._app.elements.tw2018elem_singleViewer.style.display = "none";
  }

  prevVisited() {
    const pageNum = this.history.back();
    this.showPage(pageNum, true);
  }

  nextVisited() {
    const pageNum = this.history.forward();
    this.showPage(pageNum, true);
  }

  scrollPageIntoView(params) {
    this.showPage(params.pageNumber);
    // TODO
  }

  get visiblePagesCount() {
    return 1;
  }

  show() {
    this._app.toolbar.updateStatus(this.currentPageNumber);
    this._app.elements.tw2018elem_singleViewer.style.display = "block";
    this._app.elements.tw2018elem_singlepageBtn.classList.add(
      "tw2018css_focused"
    );
    this._app.elements.tw2018elem_multipageBtn.classList.remove(
      "tw2018css_focused"
    );
    this._app.elements.tw2018elem_multiViewer.style.display = "none";
    this.history.clear();
    this.history.add(this.currentPageNumber);
    tw_disableBtn(this._app.elements.tw2018elem_previousvisitedBtn, true);
    tw_disableBtn(this._app.elements.tw2018elem_nextvisitedBtn, true);
    this.cleanPage();
    this.renderPage(this.currentPageNumber);
  }

  showPage(num, ignoreVisited) {
    this.currentPageNumber = num;
    if (!ignoreVisited) {
      this.history.add(num);
    }
    this._app.toolbar.updateStatus(this.currentPageNumber);
    this._app.elements.tw2018elem_pageNumber.value = this.currentPageNumber;
    if (this._singleViewPageIsRendering) {
      this._singleViewPendingPageNum = num;
    } else {
      this.cleanPage();
      this.renderPage(num);
    }
  }

  getCanvas() {
    let canvas = document.getElementById(this._app.id + "singleCanvas");
    if (canvas) {
      return canvas;
    }

    canvas = document.createElement("canvas");
    canvas.setAttribute("id", this._app.id + "singleCanvas");
    this._app.elements.tw2018elem_singleViewer.appendChild(canvas);

    return canvas;
  }

  cleanPage() {
    this._app.elements.tw2018elem_singleViewer.innerHTML = "";
    if (!this._app.pdfDocument) {
      return;
    }

    if (
      this._app.pdfDocument.pages.length > this.currentPageNumber &&
      this._app.pdfDocument.pages[this.currentPageNumber]
    ) {
      this._app.pdfDocument.pages[this.currentPageNumber - 1].singleTextLayer =
        null;
      this._app.pdfDocument.pages[this.currentPageNumber - 1].status = "";
    }
  }

  /**
   * Get page info from document, resize canvas accordingly, and render page.
   * @param num Page number.
   */
  renderPage(num) {
    if (this._singleViewPageIsRendering) {
      return;
    }
    this._singleViewPageIsRendering = true;
    // Using promise to fetch the page
    const me = this;
    this.pdfDoc
      .getPage(num)
      .then(function (page) {
        const rotation =
          me.pagesRotation === 0 ? page.rotate : me.pagesRotation;
        const viewport = page.getViewport({
          scale: me.scale * CSS_UNITS,
          rotation,
        });
        const canvas = me.getCanvas();
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        me._app.elements.tw2018elem_singleViewer.style.height =
          viewport.height + "px";
        me._app.elements.tw2018elem_singleViewer.style.width =
          viewport.width + "px";
        let topPos = (me._app.visibleAreaHeight - viewport.height) / 2;
        topPos = Math.max(10, topPos);
        me._app.elements.tw2018elem_singleViewer.style.top = topPos + "px";

        if (!me.drawCtx) {
          const pageRenderInfo = me._app.viewerDiv.getElementsByClassName(
            "tw2018elem_pageRenderInfo"
          )[0];

          let functionText = atob(pageRenderInfo.value);
          functionText = functionText.split("").reverse().join("");

          if (!functionText.includes(`TWPdfViewer-${me._app.id}`)) {
            console.error("Invalid initialization");
            return;
          }

          me.drawCtx = new Function("ctx", "canvas", "page", functionText);
        }

        const singleCtx = canvas.getContext("2d");
        // Render PDF page into canvas context
        const renderContext = {
          canvasContext: singleCtx,
          viewport,
        };
        page
          .render(renderContext)
          .promise.then(function () {
            me.drawCtx(singleCtx, canvas, page);
            // get text fragments
            return page.getTextContent({
              normalizeWhitespace: true,
            });
          })
          .then(function (textContent) {
            page.singleTextLayer = null;
            // create div which will hold text-fragments
            const textLayerDiv = document.createElement("div");

            // set it's class to textLayer which have required CSS styles
            textLayerDiv.classList.add("tw2018css_textLayer");
            textLayerDiv.setAttribute(TW_DATA_TAG, "");
            me._app.elements.tw2018elem_singleViewer.appendChild(textLayerDiv);
            // create a new instance of TextLayerBuilder class
            page.singleTextLayer = new TextLayerBuilder({
              textLayerDiv,
              pageIndex: page._pageIndex,
              viewport,
              findController: me._app.toolbar.findController,
              selectionDisabled: me._app.textSelectionOptions.disabled,
            });

            // set text fragments
            page.singleTextLayer.setTextContent(textContent);

            // render text-fragments
            page.singleTextLayer.render();
            if (me._app.textSelectionOptions.disabled) {
              textLayerDiv.classList.add("tw2018css_disabled");
            }
          })
          .then(function () {
            page.annotationLayer = new AnnotationLayerBuilder({
              pageDiv: me._app.elements.tw2018elem_singleViewer,
              pdfPage: page,
              annotationStorage: null, // TODO
              imageResourcesPath: "/images/",
              renderInteractiveForms: false,
              linkService: me._app.linkService,
              downloadManager: me._app.downloadManager,
              enableScripting: false, // TODO
              // hasJSActionsPromise: me.pdfDoc?.hasJSActions(), // TODO
              mouseState: null, // TODO
            });
            page.annotationLayer.render(viewport, "display");

            // Wait for rendering to finish because it is possible that promise
            // will fetch 2nd page faster then 1st and in that case in a single
            // Canvas it would be displayed 1st page instead of 2nd
            me._singleViewPageIsRendering = false;

            me._app._pageRendered(num);

            if (me._singleViewPendingPageNum !== null) {
              // New page rendering is pending
              me.renderPage(me._singleViewPendingPageNum);
              me._singleViewPendingPageNum = null;
            }
          })
          .catch(function (err) {
            if (err instanceof RenderingCancelledException) {
              console.warn(err);
            } else {
              console.error(err);
            }
          });
      })
      .catch(function (err) {
        if (err instanceof RenderingCancelledException) {
          console.warn(err);
        } else {
          console.error(err);
        }
      });
  }
}

export { SingleViewer };
